/* eslint-disable react/no-array-index-key */
import React, { useEffect, useContext, useRef } from 'react';
import {
  DealerSearchContext,
  DealerSearchContextType,
} from '@repo/contexts/DealerSearchContext';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import { cn } from '@repo/tailwind-base/utils';
import LinkComponent from '@repo/ui-components/LinkComponent';

interface MegaMenuProps {
  megaMenuIndex: number;
  megaMenuActive: number | null;
  setMegaMenuActive: (index: number | null) => void;
  keyboardNavActive: boolean;
  menuStructure: any;
  columns: number;
  getInternalLink: (link: any) => string;
}

const isExternalLink = (href: string): boolean => {
  return /^(https?:|mailto:|tel:|sms:|ftp:|\/\/)/i.test(href);
};

export default function MegaMenu({
  megaMenuIndex,
  megaMenuActive = null,
  setMegaMenuActive = () => {},
  keyboardNavActive = false,
  menuStructure,
  columns = 5,
  getInternalLink = () => '',
}: Readonly<MegaMenuProps>) {
  const { tt } = useUiComponentsInternationalContext();
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';

  const dealerSearchContext =
    useContext<DealerSearchContextType>(DealerSearchContext);
  const storedDealer = dealerSearchContext?.storedDealer ?? null;
  const toggleDealerSearch = dealerSearchContext?.toggleDealerSearch ?? null;

  const menuRef = useRef<HTMLDivElement | null>(null);
  const focusableElementsRef = useRef<HTMLElement[]>([]);

  const sections = menuStructure?.items ?? [];
  const hasHeadings = sections.some(sec => sec.title && sec.items?.length > 0);

  const titledSections = sections.filter(
    sec => sec.title && sec.items?.length > 0
  );
  const footerItems = sections.filter(sec => !sec.items?.length);
  const flatItems = !hasHeadings
    ? sections.reduce(
        (acc: any[], sec) => acc.concat(sec.items?.length ? sec.items : [sec]),
        [] as any[]
      )
    : [];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const focusables = focusableElementsRef.current;
    const currentIndex = focusables.indexOf(
      document.activeElement as HTMLElement
    );

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (currentIndex === 0) {
          e.preventDefault();
          setMegaMenuActive(null);
          document
            ?.querySelector<HTMLElement>(`#menu-trigger-${megaMenuIndex}`)
            ?.focus();
        }
      }
      if (currentIndex === focusables.length - 1) {
        e.preventDefault();
        setMegaMenuActive(null);
        document
          ?.querySelector<HTMLElement>(`#menu-trigger-${megaMenuIndex}`)
          ?.focus();
      }
    }

    if (e.key === 'Escape' || e.key === 'Esc') {
      setMegaMenuActive(null);
      document
        ?.querySelector<HTMLElement>(`#menu-trigger-${megaMenuIndex}`)
        ?.focus();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    const menu = menuRef.current;
    const nextFocused =
      e.relatedTarget || (e.nativeEvent as FocusEvent).relatedTarget;

    if (menu && nextFocused instanceof Node && !menu.contains(nextFocused)) {
      setMegaMenuActive(null);
      document
        ?.querySelector<HTMLElement>(`#menu-trigger-${megaMenuIndex}`)
        ?.focus();
    }
  };

  const renderExternalLink = (
    item: {
      title: string;
      link: {
        href: string;
      };
      blank?: boolean;
    },
    common: {
      role: string;
      onBlur: (e: React.FocusEvent) => void;
      onKeyDown: (e: React.KeyboardEvent) => void;
    }
  ) => (
    <a
      className="text-inherit no-underline hover:underline"
      href={item.link.href}
      target={item.blank ? '_blank' : '_self'}
      rel={item.blank ? 'noreferrer' : undefined}
      {...common}
    >
      {item.title}
    </a>
  );

  const getLink = (item: any, header = false) => {
    const common = {
      role: 'menuitem',
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
    };
    if (item.link?.linkType === 'internal') {
      return (
        <LinkComponent
          className={cn(
            header ? 'text-xl' : 'text-inherit no-underline hover:underline'
          )}
          to={getInternalLink({
            ...item.link,
            reference: item.link.internalLink,
          })}
          {...common}
        >
          {item.title}
        </LinkComponent>
      );
    }
    if (item.link?.href) {
      if (isExternalLink(item.link.href)) {
        return renderExternalLink(item, common);
      } else {
        return (
          <LinkComponent
            className="text-inherit no-underline hover:underline"
            to={item.link.href}
            target={item.blank ? '_blank' : '_self'}
            {...common}
          >
            {item.title}
          </LinkComponent>
        );
      }
    }
    return <span>{item?.title}</span>;
  };

  useEffect(() => {
    if (megaMenuActive !== null && keyboardNavActive && menuRef.current) {
      const focusables = Array.from(
        menuRef.current.querySelectorAll<HTMLElement>(
          'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hasAttribute('disabled'));

      focusableElementsRef.current = focusables;
      focusables[0]?.focus(); // focus first element on open
    }
  }, [keyboardNavActive, megaMenuActive]);

  return (
    <div
      ref={menuRef}
      id={`mega-menu-${megaMenuIndex}`}
      role="menu"
      aria-labelledby={`menu-trigger-${megaMenuIndex}`}
      tabIndex={0}
      onMouseEnter={() => setMegaMenuActive(megaMenuIndex)}
      onMouseLeave={() => setMegaMenuActive(null)}
      onKeyDown={handleKeyDown}
      className={cn(
        'hidden w-full bg-background p-8 absolute left-0 mt-4 top-[100px] z-20 shadow-2xl',
        megaMenuActive === megaMenuIndex && 'flex justify-center'
      )}
    >
      <ul
        className={cn(
          'list-none m-0 p-0 gap-x-24 gap-y-1.5 min-w-[42rem] max-w-screen-xl',
          hasHeadings ? `grid auto-rows-auto` : `grid grid-flow-col`
        )}
        style={{
          ...(hasHeadings
            ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
            : {
                gridTemplateRows: `repeat(${Math.ceil(
                  flatItems.length / columns
                )}, minmax(0,1fr))`,
              }),
        }}
      >
        {hasHeadings &&
          titledSections.map((sec, i) => (
            <li key={i}>
              <h2 className="text-xl">{getLink(sec, true)}</h2>
              <ul className="list-none mx-auto w-full m-0 p-0 space-y-1.5">
                {sec.items.map((it: any, j: number) => (
                  <li key={j}>{getLink(it)}</li>
                ))}
                {sec.title === 'Support' &&
                  (storedDealer ? (
                    <li>
                      <LinkComponent
                        role="menuitem"
                        className="text-inherit no-underline hover:underline"
                        to={
                          currency === 'CAD'
                            ? `/en-ca/dealer/${storedDealer.slug}`
                            : `/dealer/${storedDealer.slug}`
                        }
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                      >
                        {tt(s => s.headerCommon.yourLocalDealerLink)}
                      </LinkComponent>
                    </li>
                  ) : (
                    toggleDealerSearch && (
                      <li>
                        <LinkComponent
                          role="menuitem"
                          className="text-inherit no-underline hover:underline"
                          href="#"
                          onClick={toggleDealerSearch}
                          onBlur={handleBlur}
                          onKeyDown={handleKeyDown}
                        >
                          {tt(s => s.headerCommon.yourLocalDealerLink)}
                        </LinkComponent>
                      </li>
                    )
                  ))}
              </ul>
              {hasHeadings && footerItems.length > 0 && i + 1 === columns && (
                <ul className="list-none w-full m-0 p-0 mt-8 space-y-1.5">
                  {footerItems.map((item, k) => (
                    <li key={k} className="text-xl button-case">
                      {getLink(item)}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}

        {!hasHeadings &&
          flatItems.map((item, idx) => <li key={idx}>{getLink(item)}</li>)}
      </ul>
    </div>
  );
}
